import React, { useState } from 'react';

import moment from 'moment';

import { DatePicker, Input, Select } from 'antd';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { RemoveLuxuryHomesParams, RootState } from '@share/utils';
import { DATE_COMPLET_FORMAT_FOR_BOOKINGS } from '@constants';
import { ILuxuryHomeLocation } from '@share/common-types';
import { Routes } from '@share/constants';
import { handleNumericKeyDown } from '@utils';

import './style.scss';

export const LuxuryHomesHero: React.FC = () => {

  const [destination, setDestination] = useState<string | null>(null);
  const [guests, setGuests] = useState<string>('2');
  const [checkIn, setCheckIn] = useState<string | null | undefined>(null);
  const [checkOut, setCheckOut] = useState<string | null | undefined>(null);

  const [checkInError, setCheckInError] = useState<string | null>(null);
  const [checkOutError, setCheckOutError] = useState<string | null>(null);

  const [checkOutOpen, setCheckOutOpen] = useState<boolean>(false);

  const { locations } = useSelector((state: RootState) => state.luxuryHomesStore);
  const { account } = useSelector((state: RootState) => state.loginStore);

  const history = useHistory();

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!checkIn) {
      setCheckInError('Check-in date is required');
    }

    if (!checkOut) {
      setCheckOutError('Check-out date is required');
    }
    
    if (checkIn && checkOut && guests) {
      setCheckInError(null);
      setCheckOutError(null);

      RemoveLuxuryHomesParams();

      const url = `/${account?.name}${Routes.LuxuryHomesList}`;
      const locationParams = destination ? `location=${destination}` : '';
      const datesParams = `checkIn=${checkIn}&checkOut=${checkOut}`;
      const guestParams = guests ? `guests=${guests}` : '';
      const searchParams = ((checkIn && checkOut) || destination || guests) ? `${location.search ? '&' : '?'}${locationParams}${locationParams ? '&' : ''}${datesParams}${guestParams ? '&' : ''}${guestParams}` : '';
      history.push(`${url}${location.search}${searchParams}`);
    }
  };

  const now = new Date();
  const leadTime = account?.leadTime;
  const momentCheckInLead = !!leadTime ? moment(now).add(leadTime || 0, 'days').format('YYYY-MM-DD') : moment(now).format('YYYY-MM-DD');
  const momentCheckOut = !!checkIn ? moment(checkIn).add(1, 'days').format('YYYY-MM-DD') : null;
  
  return (
    <section className="luxury-homes-hero">
      <div className="luxury-homes-hero__content">
        <p>A curated collection of special luxury homes</p>
        <h1>EXCEPTIONAL STAYS IN</h1>
        <h1>EXTRAORDINARY DESTINATIONS</h1>
      </div>

      <div className="luxury-homes-hero__search">
        <form className="search-form" onSubmit={handleSubmit}>
          <div className="search-form__field">
            <label>Destination</label>
            <Select 
              value={destination} 
              onChange={(e) => {
                setDestination(e);
              }} 
              placeholder="Select Destination"
              filterOption={(input, option) =>
                ((option?.label ?? '') as string).toLowerCase().includes(input.toLowerCase())
              }
              options={locations?.map((d: ILuxuryHomeLocation) => ({ label: d.name, value: d.id }))}
              showSearch
              allowClear
            >
            </Select>
          </div>

          <div className="search-form__field">
            <label>Check In</label>
            <DatePicker 
              format={DATE_COMPLET_FORMAT_FOR_BOOKINGS}
              disabledDate={(date) => date.isBefore(momentCheckInLead)}
              defaultValue={momentCheckInLead ? moment(momentCheckInLead) : undefined}
              defaultPickerValue={momentCheckInLead ? moment(momentCheckInLead) : undefined}
              value={checkIn ? moment(checkIn) : null}
              onChange={(e) => {
                const date = e?.format('YYYY-MM-DD');
                if (date) {
                  setCheckOutOpen(true);
                } else {
                  setCheckOutOpen(false);
                }

                setCheckOut(null);
                setCheckOutError(null);

                setCheckIn(date);
                setCheckInError(null);
              }}
              allowClear
            />
            {checkInError && 
              <label className="search-form__field-error">{checkInError}</label>}
          </div>

          <div className="search-form__field">
            <label>Check Out</label>
            <DatePicker 
              key={momentCheckOut || moment(now).format('YYYY-MM-DD')}
              format={DATE_COMPLET_FORMAT_FOR_BOOKINGS}
              disabledDate={(date) => checkIn ? date.isBefore(moment(checkIn).add(1, 'days')) : true}
              value={checkOut ? moment(checkOut) : null}
              defaultValue={momentCheckOut ? moment(momentCheckOut) : undefined}
              defaultPickerValue={momentCheckOut ? moment(momentCheckOut) : undefined}
              open={checkOutOpen}
              dropdownClassName="check-out-dropdown"
              onChange={(e) => {
                setCheckOut(e?.format('YYYY-MM-DD'));
                setCheckOutError(null);
                setCheckOutOpen(false);
              }}
              onOpenChange={(open) => setCheckOutOpen(open)}
              allowClear
            />

            {checkOutError && 
              <label className="search-form__field-error">{checkOutError}</label>}
          </div>

          <div className="search-form__field">
            <label>Guests</label>
            <Input
              value={guests}
              onChange={(e) => {
                try {
                  const value = e.target.value;
                  if (value === '') {
                    setGuests('');
                  } else {
                    const numberValue = parseInt(value);

                    if(numberValue >= 1) {
                      setGuests(numberValue.toString());
                    } else {
                      e.preventDefault();
                    }
                  }
                } catch (error) {
                }
              }}
              onKeyDown={handleNumericKeyDown}
              placeholder="Guests"
              type="number"
              allowClear
            />
          </div>

          <button type="submit" className="search-form__submit">
            Check Availability
          </button>
        </form>
      </div>
    </section>
  );
};
